import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

export const SUPPORTED_CURRENCIES = ['USD', 'EUR', 'PLN', 'CHF', 'GBP', 'CNY', 'JPY', 'AUD'] as const;
export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number];

@Injectable({providedIn: 'root'})
export class CurrencyPreferenceService {
  private readonly storageKey = 'artsaas.selectedCurrency';
  private readonly euroRegions = new Set([
    'AT', 'BE', 'BG', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES',
  ]);

  /**
   * AT-9160: parametr `currency` ma sens tylko gdy na stronie/w menu/w stopce istnieje
   * komponent zmiany waluty — backend potwierdza to osobno w odpowiedzi strony, menu
   * i stopki (`shouldConvertCurrency`). Te trzy requesty lecą niezależnie i kończą się
   * w nieprzewidywalnej kolejności, więc trzymamy TRZY osobne sloty zamiast jednej
   * flagi — inaczej ten, który akurat odpowie jako ostatni, nadpisywałby wynik
   * poprzednich (np. stopka bez komponentu zgasiłaby prawdę ustaloną przez menu).
   * Efektywna wartość to OR tych trzech. Domyślnie `true` (fail-open) — przed
   * pierwszą odpowiedzią wciąż dokładamy parametr; backend i tak niezależnie
   * sprawdza obecność komponentu i ignoruje param, gdy go nie ma.
   */
  private pageHasSwitcher = true;
  private menuHasSwitcher = true;
  private footerHasSwitcher = true;

  private get shouldConvertCurrency(): boolean {
    return this.pageHasSwitcher || this.menuHasSwitcher || this.footerHasSwitcher;
  }

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
  }

  setPageShouldConvertCurrency(value: boolean | undefined | null): void {
    if (typeof value === 'boolean') {
      this.pageHasSwitcher = value;
    }
  }

  setMenuShouldConvertCurrency(value: boolean | undefined | null): void {
    if (typeof value === 'boolean') {
      this.menuHasSwitcher = value;
    }
  }

  setFooterShouldConvertCurrency(value: boolean | undefined | null): void {
    if (typeof value === 'boolean') {
      this.footerHasSwitcher = value;
    }
  }

  getCurrentCurrency(availableCurrencies?: string[]): SupportedCurrency {
    const available = this.normalizeAvailableCurrencies(availableCurrencies);
    const manual = this.getManualCurrency();
    const resolved = manual ?? this.resolveBrowserCurrency(available);

    return available.includes(resolved) ? resolved : available[0];
  }

  setManualCurrency(currency: string): void {
    const normalized = this.normalizeCurrency(currency);
    if (!normalized || !isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.setItem(this.storageKey, normalized);
  }

  getManualCurrency(): SupportedCurrency | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    return this.normalizeCurrency(localStorage.getItem(this.storageKey));
  }

  resolveBrowserCurrency(availableCurrencies?: string[]): SupportedCurrency {
    return this.resolveLanguageTagCurrency(this.getBrowserLanguageTag(), availableCurrencies);
  }

  resolveLanguageTagCurrency(languageTag: string, availableCurrencies?: string[]): SupportedCurrency {
    const [language = '', region = ''] = languageTag.replace('_', '-').split('-');
    const normalizedLanguage = language.toLowerCase();
    const normalizedRegion = region.toUpperCase();
    const mapped = this.mapLanguageRegion(normalizedLanguage, normalizedRegion);
    const available = this.normalizeAvailableCurrencies(availableCurrencies);

    return available.includes(mapped) ? mapped : available[0];
  }

  /**
   * Koszyk/zamówienie celowo NIE korzystają z gatingu AT-9160 (patrz
   * `appendCurrencyToUrlIfSwitcherExists`) — dokładają walutę tak jak zawsze,
   * niezależnie od `shouldConvertCurrency`. Ten stan jest globalny i ustawiany
   * przy każdej nawigacji (strona/menu/stopka), więc gdyby koszyk/zamówienie
   * też go respektowały, zakładka wybrana wcześniej przez odwiedzającego mogłaby
   * przestać być wysyłana tylko dlatego, że menu/stopka dociągnięte na stronie
   * koszyka akurat nie zawierają komponentu zmiany waluty.
   */
  appendCurrencyToUrl(url: string, availableCurrencies?: string[]): string {
    const [baseWithPath, hash = ''] = url.split('#');
    const [path, query = ''] = baseWithPath.split('?');
    const params = new URLSearchParams(query);
    params.set('currency', this.getCurrentCurrency(availableCurrencies));
    const queryString = params.toString();

    return `${path}${queryString ? '?' + queryString : ''}${hash ? '#' + hash : ''}`;
  }

  withCurrencyParam(params: URLSearchParams, availableCurrencies?: string[]): URLSearchParams {
    const nextParams = new URLSearchParams(params.toString());
    nextParams.set('currency', this.getCurrentCurrency(availableCurrencies));

    return nextParams;
  }

  /**
   * AT-9160: wariant dla stron/list/menu/stopki — parametr `currency` dokładany
   * tylko gdy backend potwierdził (`shouldConvertCurrency`), że komponent zmiany
   * waluty faktycznie istnieje w bieżącym kontekście.
   */
  appendCurrencyToUrlIfSwitcherExists(url: string, availableCurrencies?: string[]): string {
    return this.shouldConvertCurrency ? this.appendCurrencyToUrl(url, availableCurrencies) : url;
  }

  withCurrencyParamIfSwitcherExists(params: URLSearchParams, availableCurrencies?: string[]): URLSearchParams {
    return this.shouldConvertCurrency
      ? this.withCurrencyParam(params, availableCurrencies)
      : new URLSearchParams(params.toString());
  }

  normalizeAvailableCurrencies(currencies?: string[]): SupportedCurrency[] {
    const normalized = (currencies ?? [])
      .map(currency => this.normalizeCurrency(currency))
      .filter((currency): currency is SupportedCurrency => !!currency);

    return normalized.length ? normalized : [...SUPPORTED_CURRENCIES];
  }

  private normalizeCurrency(currency: string | null): SupportedCurrency | null {
    const normalized = (currency ?? '').trim().toUpperCase();

    return (SUPPORTED_CURRENCIES as readonly string[]).includes(normalized)
      ? normalized as SupportedCurrency
      : null;
  }

  private getBrowserLanguageTag(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return 'en-US';
    }

    return (navigator.languages?.[0] || navigator.language || 'en-US').replace('_', '-');
  }

  private mapLanguageRegion(language: string, region: string): SupportedCurrency {
    if (language === 'pl') {
      return 'PLN';
    }
    if (language === 'en' && region === 'GB') {
      return 'GBP';
    }
    if (language === 'en' && region === 'AU') {
      return 'AUD';
    }
    if (language === 'en' && region === 'US') {
      return 'USD';
    }
    if (language === 'de' && region === 'CH') {
      return 'CHF';
    }
    if (language === 'zh') {
      return 'CNY';
    }
    if (language === 'ja') {
      return 'JPY';
    }
    if (this.euroRegions.has(region)) {
      return 'EUR';
    }

    return language === 'pl' ? 'PLN' : 'USD';
  }
}
